import { useState, useEffect } from 'react'
import { useNavigate, Navigate, useSearchParams } from 'react-router-dom'
import { useTheme } from '@/store/useTheme'
import { useSession } from '@/store/useSession'
import type { WotSelection } from '@/store/sessionTypes'
import { PALETTES } from '@/store/palettes'
import { enhancePaletteForHighContrast } from '@/utils/highContrastPalette'
import styles from './BeforeWeBeginPage.module.css'

// ── Storage keys ────────────────────────────────────────────────────────
const KEY_DONE         = 'fitutogether-practices-done'
const KEY_PRACTICES    = 'fitutogether-practices'
const KEY_OWN_PRACTICE = 'fitutogether-own-practice'
const KEY_BWB_PALETTE  = 'bwb-palette'

interface OwnPracticeData { text: string; paletteId: string }

function readOwnPractice(): OwnPracticeData | null {
  try {
    const raw = localStorage.getItem(KEY_OWN_PRACTICE)
    return raw ? (JSON.parse(raw) as OwnPracticeData) : null
  } catch { return null }
}

// ── Practice options ────────────────────────────────────────────────────
const PRACTICES = [
  { id: 'mood-check',    label: 'A mood check',          subtitle: 'Check in with yourself before and after the session — privately, or together with your client as a shared starting point.' },
  { id: 'breathing',     label: 'A breathing exercise',  subtitle: 'A simple 4:6 breath'                                                 },
  { id: 'box-breathing', label: 'Box breathing',         subtitle: '4:4:4:4 — steady and energising'                                    },
  { id: 'grounding',     label: 'A grounding practice',  subtitle: 'A moment to land'                                                    },
  { id: 'own',           label: 'Something of my own',   subtitle: 'I know what works for me'                                            },
] as const

type PracticeId = typeof PRACTICES[number]['id']

const PRACTICE_PROMPTS: Record<string, string> = {
  grounding: 'Feel your feet on the floor. Notice where you are.',
  own:       'Take your moment.',
}

type Step = 'menu' | 'guide' | 'done'

// ── Window of Tolerance options ─────────────────────────────────────────
const WOT_OPTIONS: { id: WotSelection; label: string; subtitle: string }[] = [
  { id: 'hyper', label: 'Hyper', subtitle: 'Anxious, busy, hard to settle'      },
  { id: 'present',   label: 'Present',   subtitle: 'Grounded and ready'                 },
  { id: 'flat',      label: 'Flat',      subtitle: 'Low, disconnected, hard to engage'  },
]

// ── Practice suggestions per WoT state ──────────────────────────────────
const WOT_SUGGESTIONS: Record<WotSelection, PracticeId[]> = {
  hyper: ['mood-check', 'breathing', 'grounding'],
  flat:      ['mood-check', 'box-breathing'],
  present:   ['mood-check'],
}

// ── Contextual response to each WoT choice ──────────────────────────────
const WOT_MESSAGES: Record<WotSelection, string> = {
  hyper: "Let's get you ready for your day with a mood check and one of the practices below.",
  flat:      "Let's get you ready for your day with a mood check and one of the practices below.",
  present:   "You're in a good place. Choose something from the menu below, or go straight in.",
}

// Reflective note — hidden until session history is built.
// TODO: Show this automatically after a configurable number of sessions
// (e.g. 5+) once session-history persistence is implemented.
// const REFLECTIVE_NOTE =
//   `There's no right answer here. If today is already feeling hard, that's
//    important to notice. You only need to be good enough — present enough to
//    hold the space. Caring in any capacity is hard. This programme isn't just
//    about helping someone else become more aware — it's about you doing the
//    same for yourself. Over time, the pattern is data worth paying attention
//    to. If it's showing you consistently outside your window of tolerance, it
//    might be worth finding someone you trust and using the peer-to-peer version
//    of FitUTogether together — and thinking through how to bolster your own
//    resources.`

// Text for the "What if this feels like a pattern?" expandable panel
const PATTERN_PARA_1 =
  `There's no right answer here. If today is already feeling hard, that's important to notice. You only need to be good enough — present enough to hold the space. Caring in any capacity is hard. This programme isn't just about helping someone else become more aware — it's about you doing the same for yourself.`

const PATTERN_PARA_2 =
  `Over time, the pattern is data worth paying attention to. If it's showing you consistently outside your window of tolerance, it might be worth finding someone you trust and using the peer-to-peer version of FitUTogether together — and thinking through how to bolster your own resources.`

// ── Window of Tolerance check ────────────────────────────────────────────
function WoTCheck({
  selected,
  onSelect,
}: {
  selected:  WotSelection | null
  onSelect:  (id: WotSelection) => void
}) {
  const [patternOpen, setPatternOpen] = useState(false)

  // Close the panel whenever the selection changes
  const handleSelect = (id: WotSelection) => {
    if (id !== selected) setPatternOpen(false)
    onSelect(id)
  }

  const showPatternBtn = selected === 'hyper' || selected === 'flat'

  return (
    <section className={styles.wotSection} aria-labelledby="wot-heading">
      <header className={styles.sectionHeader}>
        <h2 id="wot-heading" className={styles.question}>
          Before we begin, take a moment.
        </h2>
        <p className={styles.subtitle}>Where are you right now?</p>
      </header>

      <div className={styles.wotCards} role="group" aria-label="Window of Tolerance check">
        {WOT_OPTIONS.map(opt => (
          <button
            key={opt.id}
            className={styles.wotCard}
            data-selected={selected === opt.id}
            aria-pressed={selected === opt.id}
            onClick={() => handleSelect(opt.id)}
          >
            <span className={styles.wotLabel}>{opt.label}</span>
            <span className={styles.wotSub}>{opt.subtitle}</span>
          </button>
        ))}
      </div>

      {selected !== null && (
        <p className={styles.wotMessage} aria-live="polite">
          {WOT_MESSAGES[selected]}
        </p>
      )}

      {/* Pattern button — only for Hyper or Flat */}
      {showPatternBtn && (
        <div className={styles.patternSection}>
          <button
            className={styles.patternBtn}
            aria-expanded={patternOpen}
            onClick={() => setPatternOpen(o => !o)}
          >
            What if this feels like a pattern?
          </button>

          {patternOpen && (
            <div className={styles.patternPanel} aria-live="polite">
              <p className={styles.patternText}>{PATTERN_PARA_1}</p>
              <p className={styles.patternText}>{PATTERN_PARA_2}</p>
            </div>
          )}
        </div>
      )}

      {/* Reflective note hidden — show after session history is built.
          See PATTERN_PARA_1 / PATTERN_PARA_2 and the commented constant above. */}
    </section>
  )
}

// ── PANAS items (Watson et al., 1988 standard order) ────────────────────
const PANAS_ITEMS: { word: string; type: 'positive' | 'negative' }[] = [
  { word: 'Interested',   type: 'positive' },
  { word: 'Distressed',   type: 'negative' },
  { word: 'Excited',      type: 'positive' },
  { word: 'Upset',        type: 'negative' },
  { word: 'Strong',       type: 'positive' },
  { word: 'Guilty',       type: 'negative' },
  { word: 'Scared',       type: 'negative' },
  { word: 'Hostile',      type: 'negative' },
  { word: 'Enthusiastic', type: 'positive' },
  { word: 'Proud',        type: 'positive' },
  { word: 'Irritable',    type: 'negative' },
  { word: 'Alert',        type: 'positive' },
  { word: 'Ashamed',      type: 'negative' },
  { word: 'Inspired',     type: 'positive' },
  { word: 'Nervous',      type: 'negative' },
  { word: 'Determined',   type: 'positive' },
  { word: 'Attentive',    type: 'positive' },
  { word: 'Jittery',      type: 'negative' },
  { word: 'Active',       type: 'positive' },
  { word: 'Afraid',       type: 'negative' },
]

// ── Scale labels (used on card back, descriptor box, and aria) ───────────
const SCALE_LABELS: Record<number, string> = {
  1: 'Very slightly or not at all',
  2: 'A little',
  3: 'Moderately',
  4: 'Quite a bit',
  5: 'Extremely',
}

// ── Score words shown on the card back (no numerals) ─────────────────────
const SCORE_WORDS: Record<number, string> = {
  1: 'ONE',
  2: 'TWO',
  3: 'THREE',
  4: 'FOUR',
  5: 'FIVE',
}

// Duration of a single 180° card rotation
const FLIP_MS = 420

// ── Score result display ─────────────────────────────────────────────────
function ScoreDisplay({
  score,
  onContinue,
}: {
  score:      number
  onContinue: () => void
}) {
  const context =
    score >= 70
      ? "You're showing up well today. That energy matters."
      : score >= 40
        ? "You're here, and that's what counts."
        : "It's okay to feel how you feel. You're still here."

  return (
    <div className={styles.scoreWrap} aria-live="polite">
      <p className={styles.scoreLabel}>Your mood today</p>
      <p className={styles.scoreValue}>{score}%</p>
      <p className={styles.scoreContext}>{context}</p>
      <button className={styles.continueBtn} onClick={onContinue}>
        Continue
      </button>
    </div>
  )
}

// ── PANAS mood check ─────────────────────────────────────────────────────
function PANASCheck({ onComplete }: { onComplete: () => void }) {
  const { setPanasScore } = useSession()

  // ── Card display state ───────────────────────────────────────────────
  const [frontWord,     setFrontWord]     = useState(PANAS_ITEMS[0].word)
  const [frontType,     setFrontType]     = useState(PANAS_ITEMS[0].type)
  const [selectedScore, setSelectedScore] = useState<number | null>(null)
  // isFlipped: drives the CSS transition — true = back face visible
  const [isFlipped,     setIsFlipped]     = useState(false)

  // ── Nav animation state (arrow buttons only) ─────────────────────────
  const [navKey, setNavKey] = useState(0)
  const [navDir, setNavDir] = useState<'init' | 'fwd' | 'back'>('init')

  // ── Shared busy gate ─────────────────────────────────────────────────
  const [busy, setBusy] = useState(false)

  // ── Descriptor button-hover state ───────────────────────────────────
  const [hoveredValue, setHoveredValue] = useState<number | null>(null)

  // ── PANAS data ───────────────────────────────────────────────────────
  const [index,   setIndex]   = useState(0)
  const [answers, setAnswers] = useState<Record<number, number>>({})
  const [done,    setDone]    = useState(false)
  const [score,   setScore]   = useState(0)

  // ── Navigate to a card ───────────────────────────────────────────────
  // Two animation paths depending on whether the card is currently flipped:
  //   flipped  → CSS transition (flip back to front, new word revealed)
  //   unflipped → key-change nav animation (directional slide-flip)
  const goTo = (newIdx: number, dir: 'fwd' | 'back') => {
    if (busy || newIdx < 0 || newIdx >= 20) return
    setBusy(true)
    setFrontWord(PANAS_ITEMS[newIdx].word)
    setFrontType(PANAS_ITEMS[newIdx].type)
    setIndex(newIdx)
    // Restore the saved answer for the destination card (null if unanswered)
    setSelectedScore(answers[newIdx] ?? null)

    if (isFlipped) {
      // Flip the card back to front — the new word is now on the front face
      setIsFlipped(false)
      setTimeout(() => setBusy(false), FLIP_MS + 40)
    } else {
      // Slide/flip-in via nav animation (key remount, fires exactly once)
      setNavDir(dir)
      setNavKey(k => k + 1)
      setTimeout(() => setBusy(false), 300)
    }
  }

  // ── Select a response ────────────────────────────────────────────────
  // Records the answer. If the card is on its front, flips to back to show
  // the chosen score. If already on its back (re-selecting), updates silently.
  const handleAnswer = (value: number) => {
    if (busy) return
    setAnswers(prev => ({ ...prev, [index]: value }))
    setSelectedScore(value)

    if (!isFlipped) {
      setBusy(true)
      setIsFlipped(true)
      setTimeout(() => setBusy(false), FLIP_MS + 40)
    }
    // Already flipped: score on back updates in place, no animation needed
  }

  // ── Forward: advance to next card, or finalise on last card ──────────
  const handleFwd = () => {
    if (!canFwd) return
    if (index === 19) {
      finalize({ ...answers })
    } else {
      goTo(index + 1, 'fwd')
    }
  }

  // ── Card area hover / touch handlers ────────────────────────────────
  // The whole .panas wrapper is the "card area" — moving the mouse outside
  // it flips the card to front; moving back in flips to back (if answered).
  const handleAreaEnter = () => {
    if (!busy && selectedScore !== null && !isFlipped) setIsFlipped(true)
  }
  const handleAreaLeave = () => {
    if (!busy && selectedScore !== null && isFlipped)  setIsFlipped(false)
  }
  // Touch tap on the card itself toggles between faces.
  const handleCardToggle = () => {
    if (!busy && selectedScore !== null) setIsFlipped(f => !f)
  }

  const finalize = (all: Record<number, number>) => {
    let pa = 0, na = 0
    PANAS_ITEMS.forEach((item, i) => {
      const v = all[i] ?? 1
      if (item.type === 'positive') pa += v
      else na += v
    })
    // Normalise to 0–100: raw range is -40 to +40
    const pct = Math.round(((pa - na) + 40) / 80 * 100)
    setScore(pct)
    setPanasScore(pct)
    setDone(true)
  }

  if (done) {
    return <ScoreDisplay score={score} onContinue={onComplete} />
  }

  const currentAnswer   = answers[index]
  // Forward: enabled once this card has been answered (regardless of flip state)
  const canFwd  = currentAnswer !== undefined && !busy
  const canBack = index > 0 && !busy

  // Descriptor: hover takes priority, then selected answer, then empty
  const descriptorText =
    hoveredValue  !== null            ? SCALE_LABELS[hoveredValue]
    : currentAnswer !== undefined     ? SCALE_LABELS[currentAnswer]
    : ''

  return (
    <div className={styles.panas}>

      {/* Header row — label + progress */}
      <div className={styles.panasHeader}>
        <p className={styles.panasHeaderLabel}>Before we begin</p>
        <p className={styles.panasProgress} aria-label={`Card ${index + 1} of 20`}>
          {index + 1} <span className={styles.panasProgressOf}>/ 20</span>
        </p>
      </div>

      {/* Progress track */}
      <div className={styles.progressTrack} aria-hidden="true">
        <div
          className={styles.progressFill}
          style={{ width: `${((index + 1) / 20) * 100}%` }}
        />
      </div>

      {/* Card stage ─────────────────────────────────────────────────────
          key={navKey}: changes only for arrow nav → CSS animation plays once.
          data-flipped: drives CSS transition for the score-reveal flip.
          These two mechanisms never overlap (busy gate prevents it).
      ──────────────────────────────────────────────────────────────────── */}
      <div
        className={styles.cardStage}
        onClick={handleCardToggle}
        onMouseEnter={handleAreaEnter}
        onMouseLeave={handleAreaLeave}
        style={{ cursor: selectedScore !== null ? 'pointer' : 'default' }}
        aria-label={selectedScore !== null ? 'Tap to flip card' : undefined}
        role={selectedScore !== null ? 'button' : undefined}
      >
        <div
          key={navKey}
          data-dir={navDir}
          data-flipped={isFlipped ? 'true' : 'false'}
          className={styles.cardContainer}
        >
          <div
            className={styles.cardFront}
            data-type={frontType}
            aria-live="polite"
            aria-atomic="true"
          >
            {frontWord}
          </div>
          <div className={styles.cardBack} data-type={frontType} aria-hidden="true">
            {selectedScore !== null && (
              <>
                <span className={styles.backWord}>
                  {SCORE_WORDS[selectedScore]}
                </span>
                <span className={styles.backLabel}>
                  {SCALE_LABELS[selectedScore].toUpperCase()}
                </span>
              </>
            )}
          </div>
        </div>
      </div>

      {/* Response buttons, descriptor, and nav — all in one group
          so the gap above and below the descriptor box is identical */}
      <div className={styles.interactionArea}>

        <div
          className={styles.responseRow}
          role="group"
          aria-label={`How much do you feel ${frontWord.toLowerCase()} right now?`}
        >
          {([1, 2, 3, 4, 5] as const).map(v => (
            <button
              key={v}
              className={styles.responseBtn}
              data-selected={currentAnswer === v}
              onClick={() => handleAnswer(v)}
              disabled={busy}
              aria-label={SCALE_LABELS[v]}
              aria-pressed={currentAnswer === v}
              onMouseEnter={() => setHoveredValue(v)}
              onMouseLeave={() => setHoveredValue(null)}
              onFocus={()    => setHoveredValue(v)}
              onBlur={()     => setHoveredValue(null)}
            >
              {v}
            </button>
          ))}
        </div>

        {/* Descriptor box — constrained width so it reads closer to square */}
        <div
          className={styles.descriptorBox}
          aria-live="polite"
          aria-atomic="true"
        >
          <span className={styles.descriptorText}>{descriptorText}</span>
        </div>

        {/* Navigation arrows */}
        <div className={styles.navRow}>
          <button
            className={styles.navBtn}
            onClick={() => goTo(index - 1, 'back')}
            disabled={!canBack}
            aria-label="Previous word"
          >
            ←
          </button>
          <button
            className={styles.navBtn}
            onClick={handleFwd}
            disabled={!canFwd}
            aria-label={index === 19 ? 'See your score' : 'Next word'}
          >
            →
          </button>
        </div>

      </div>

    </div>
  )
}

// ── Generic practice prompt (non-breathing, non-PANAS) ───────────────────
function SimpleGuide({
  practice,
  onComplete,
}: {
  practice:   { label: string; id: string }
  onComplete: () => void
}) {
  return (
    <div className={styles.simpleGuide} aria-live="polite">
      <p className={styles.simpleGuideLabel}>{practice.label}</p>
      <p className={styles.simpleGuideText}>
        {PRACTICE_PROMPTS[practice.id] ?? 'Take a moment.'}
      </p>
      <button className={styles.continueBtn} onClick={onComplete}>
        I'm ready
      </button>
    </div>
  )
}

// ── "Something of my own" practice ──────────────────────────────────────
function OwnPracticeGuide({ onComplete }: { onComplete: () => void }) {
  const { palette: themePalette } = useTheme()

  const saved = readOwnPractice()
  const defaultPaletteId = saved?.paletteId ?? themePalette.id

  const [text,      setText]      = useState(saved?.text ?? '')
  const [paletteId, setPaletteId] = useState(defaultPaletteId)
  const [editing,   setEditing]   = useState(!saved?.text)

  const pal = PALETTES.find(p => p.id === paletteId) ?? PALETTES[0]

  function handleContinue() {
    localStorage.setItem(KEY_OWN_PRACTICE, JSON.stringify({ text: text.trim(), paletteId }))
    onComplete()
  }

  const containerStyle: React.CSSProperties = {
    background:   pal.bg,
    borderColor:  `color-mix(in srgb, ${pal.ring} 40%, transparent)`,
    color:        pal.text,
  }

  const textareaStyle: React.CSSProperties = {
    background:  `color-mix(in srgb, ${pal.swatch} 60%, ${pal.bg})`,
    borderColor: `color-mix(in srgb, ${pal.ring} 35%, transparent)`,
    color:       pal.text,
    resize:      'none',
  }

  return (
    <div className={styles.ownPractice} style={containerStyle}>
      <div className={styles.ownPracticeHeader}>
        <h2 className={styles.ownPracticeHeading}>Your practice</h2>
        {saved?.text && !editing && (
          <button
            className={styles.ownEditLink}
            onClick={() => setEditing(true)}
            type="button"
          >
            edit
          </button>
        )}
      </div>

      <p className={styles.ownPracticeSub}>
        What helps you arrive? Write it here and it will be waiting for you each time.
      </p>

      <textarea
        className={styles.ownPracticeTextarea}
        style={textareaStyle}
        placeholder="Describe what you do…"
        value={text}
        onChange={e => setText(e.target.value)}
        readOnly={!editing}
        rows={5}
        aria-label="Your own practice description"
      />

      <div className={styles.ownPaletteRow} role="group" aria-label="Background colour">
        {PALETTES.map(p => (
          <button
            key={p.id}
            type="button"
            className={styles.ownPaletteSwatch}
            style={{
              background:  p.swatch,
              outline:     paletteId === p.id ? `2px solid ${p.ring}` : '2px solid transparent',
              outlineOffset: '2px',
            }}
            aria-label={p.label}
            aria-pressed={paletteId === p.id}
            onClick={() => setPaletteId(p.id)}
          >
            {paletteId === p.id && (
              <svg className={styles.ownSwatchTick} viewBox="0 0 10 10" aria-hidden="true">
                <polyline points="2,5 4.2,7.5 8,3" fill="none" stroke={p.text} strokeWidth="1.6" strokeLinecap="round" strokeLinejoin="round" />
              </svg>
            )}
          </button>
        ))}
      </div>

      <button className={styles.continueBtn} onClick={handleContinue}>
        Continue
      </button>
    </div>
  )
}

// ── Practice guide — runs a single practice ─────────────────────────────
function PracticeGuide({
  practiceId,
  onAllDone,
}: {
  practiceId: PracticeId
  onAllDone:    () => void
}) {
  const current   = PRACTICES.find(p => p.id === practiceId)

  // Navigate to standalone pages — they return with ?practice-done=1
  if (practiceId === 'breathing')     return <Navigate to="/breathe/four-six?return=/before-we-begin" replace />
  if (practiceId === 'box-breathing') return <Navigate to="/breathe/box?return=/before-we-begin"       replace />
  if (practiceId === 'grounding')     return <Navigate to="/grounding?return=/before-we-begin"          replace />

  if (!current) return null

  const advance = () => onAllDone()

  if (practiceId === 'mood-check') return <PANASCheck onComplete={advance} />
  if (practiceId === 'own')        return <OwnPracticeGuide onComplete={advance} />
  return <SimpleGuide practice={current} onComplete={advance} />
}

// ── Post-practice landing screen ─────────────────────────────────────────
function PostPracticeScreen({
  onTrySomethingElse,
  onContinue,
}: {
  onTrySomethingElse: () => void
  onContinue:         () => void
}) {
  return (
    <div className={styles.postPractice} aria-live="polite">
      <p className={styles.postPracticeMsg}>
        Good. How are you feeling now? Ready to go or would you like to try something else?
      </p>
      <div className={styles.postPracticeActions}>
        <button className={styles.continueBtn} onClick={onTrySomethingElse}>
          Try something else
        </button>
        <button className={styles.continueToClientsLink} onClick={onContinue}>
          Continue to my clients →
        </button>
      </div>
    </div>
  )
}

// ── Practice menu — tap a card to start immediately ──────────────────────
function PracticeMenu({
  onSelect,
  onGoToClients,
  suggested = [],
}: {
  onSelect:      (id: PracticeId) => void
  onGoToClients: () => void
  suggested?:    PracticeId[]
}) {
  return (
    <section className={styles.section} aria-labelledby="practices-heading">
      <header className={styles.sectionHeader}>
        <h2 id="practices-heading" className={styles.question}>
          What helps you arrive?
        </h2>
        <p className={styles.subtitle}>
          Choose what helps you show up well today.
        </p>
      </header>

      <div className={styles.cardGrid} role="group" aria-label="Practice options">
        {PRACTICES.map(p => {
          const isSuggested = suggested.includes(p.id)
          return (
            <button
              key={p.id}
              type="button"
              className={styles.practiceCard}
              data-suggested={isSuggested}
              onClick={() => onSelect(p.id)}
            >
              <span className={styles.cardBody}>
                <span className={styles.cardLabel}>{p.label}</span>
                <span className={styles.cardSub}>{p.subtitle}</span>
              </span>
              {isSuggested && (
                <span className={styles.suggestedChip} aria-label="suggested">
                  suggested
                </span>
              )}
            </button>
          )
        })}
      </div>

      <button className={styles.continueToClientsLink} onClick={onGoToClients}>
        Continue to my clients →
      </button>
    </section>
  )
}

// ── Page ─────────────────────────────────────────────────────────────────

function PaletteTick() {
  return (
    <svg width="10" height="10" viewBox="0 0 12 12" fill="none"
      stroke="currentColor" strokeWidth="2.2"
      strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
      <polyline points="2 6 5 9 10 3" />
    </svg>
  )
}

export default function BeforeWeBeginPage() {
  const navigate                           = useNavigate()
  const [searchParams, setSearchParams]    = useSearchParams()
  const { highContrast }                   = useTheme()
  const { setWotSelection, wotSelection: sessionWot } = useSession()

  // Restore WoT from session context so returning from an external practice
  // preserves the selection without needing re-input.
  const [wotChoice, setWotChoice] = useState<WotSelection | null>(sessionWot)
  const [step, setStep]           = useState<Step>('menu')

  // External practices return with ?practice-done=1 — show landing, not the menu.
  useEffect(() => {
    if (searchParams.get('practice-done') === '1') {
      setStep('done')
    }
  }, [searchParams])

  const clearPracticeDoneParam = () => {
    if (!searchParams.has('practice-done')) return
    const next = new URLSearchParams(searchParams)
    next.delete('practice-done')
    setSearchParams(next, { replace: true })
  }

  const [activePractice, setActivePractice] = useState<PracticeId | null>(null)

  const [bwbPaletteId, setBwbPaletteId] = useState(() => {
    return localStorage.getItem(KEY_BWB_PALETTE) ?? 'white'
  })

  const bwbBasePalette = PALETTES.find(p => p.id === bwbPaletteId) ?? PALETTES[0]
  const bwbPalette = highContrast
    ? enhancePaletteForHighContrast(bwbBasePalette)
    : bwbBasePalette

  const handleBwbPaletteSelect = (id: string) => {
    setBwbPaletteId(id)
    localStorage.setItem(KEY_BWB_PALETTE, id)
  }

  const handleWotSelect = (id: WotSelection) => {
    setWotChoice(id)
    setWotSelection(id)
  }

  const saveLastPractice = (id: PracticeId) => {
    try {
      localStorage.setItem(KEY_PRACTICES, JSON.stringify([id]))
      localStorage.setItem(KEY_DONE, 'true')
    } catch { /* quota — fail silently */ }
  }

  const handleSelectPractice = (id: PracticeId) => {
    saveLastPractice(id)
    setActivePractice(id)
    setStep('guide')
  }

  const handleContinueToClients = () => {
    navigate('/clients')
  }

  const paletteVars = {
    '--bwb-bg':   bwbPalette.bg,
    '--bwb-text': bwbPalette.text,
    '--bwb-ring': bwbPalette.ring,
    background:   bwbPalette.bg,
  } as React.CSSProperties

  return (
    <main className={styles.page} style={paletteVars} aria-label="Before we begin">
      <div className={styles.paletteBar} role="group" aria-label="Page colour">
        {PALETTES.map(p => (
          <button
            key={p.id}
            type="button"
            className={styles.paletteCircle}
            style={{ background: p.swatch, color: p.text }}
            data-active={bwbPaletteId === p.id}
            onClick={() => handleBwbPaletteSelect(p.id)}
            aria-label={p.label}
            aria-pressed={bwbPaletteId === p.id}
            title={p.label}
          >
            {bwbPaletteId === p.id && <PaletteTick />}
          </button>
        ))}
      </div>

      <div className={styles.inner}>

        {/* Done screen — shown after each practice completes */}
        {step === 'done' && (
          <PostPracticeScreen
            onTrySomethingElse={() => {
              clearPracticeDoneParam()
              setStep('menu')
            }}
            onContinue={() => {
              clearPracticeDoneParam()
              handleContinueToClients()
            }}
          />
        )}

        {/* Guide step — focused view while working through inline practices */}
        {step === 'guide' && activePractice && (
          <>
            <PracticeGuide
              practiceId={activePractice}
              onAllDone={() => setStep('done')}
            />
            <button
              className={styles.continueToClientsLink}
              onClick={handleContinueToClients}
            >
              Continue to my clients →
            </button>
          </>
        )}

        {/* Menu step: WoT check, then full practice menu */}
        {step === 'menu' && (
          <>
            <WoTCheck selected={wotChoice} onSelect={handleWotSelect} />

            {wotChoice !== null && (
              <div className={styles.practiceSection}>
                <PracticeMenu
                  onSelect={handleSelectPractice}
                  onGoToClients={handleContinueToClients}
                  suggested={WOT_SUGGESTIONS[wotChoice]}
                />
              </div>
            )}
          </>
        )}

      </div>
    </main>
  )
}
